/**
 * Task A2A-C (#1961) — Tipi Zod dell'Agente Architetto (orchestratore A2A).
 *
 * Contratto unico tra backend e frontend. Niente `any`: ogni payload HTTP
 * passa per uno Zod schema al boundary.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
// Task #2997 (T8) — `custom` rende l'Architetto un cittadino di prima classe del
// Custom AI del tenant. I 4 provider prime restano invariati (pinning).
export const ARCHITECT_PROVIDERS = ['openai', 'claude', 'gemini', 'custom'] as const;
export type ArchitectProvider = (typeof ARCHITECT_PROVIDERS)[number];
export const ArchitectProviderSchema = z.enum(ARCHITECT_PROVIDERS);

export const ArchitectConfigSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  provider: ArchitectProviderSchema,
  model: z.string().min(1).max(128),
  system_prompt: z.string().max(20_000),
  max_hops: z.number().int().min(1).max(30),
  max_tokens_per_run: z.number().int().min(500).max(1_000_000),
  timeout_seconds: z.number().int().min(5).max(1800),
  prefilter_top_k: z.number().int().min(1).max(200),
  cycle_policy: z.number().int().min(1).max(10),
  is_enabled: z.boolean(),
  // Task #2997 (T8) — riferimenti Custom AI valorizzati solo quando provider='custom'.
  custom_endpoint_id: z.string().uuid().nullable(),
  custom_model_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ArchitectConfig = z.infer<typeof ArchitectConfigSchema>;

export const ArchitectConfigUpdateInputSchema = z.object({
  provider: ArchitectProviderSchema.optional(),
  model: z.string().min(1).max(128).optional(),
  system_prompt: z.string().max(20_000).optional(),
  max_hops: z.number().int().min(1).max(30).optional(),
  max_tokens_per_run: z.number().int().min(500).max(1_000_000).optional(),
  timeout_seconds: z.number().int().min(5).max(1800).optional(),
  prefilter_top_k: z.number().int().min(1).max(200).optional(),
  cycle_policy: z.number().int().min(1).max(10).optional(),
  is_enabled: z.boolean().optional(),
  // Task #2997 (T8) — `null` consente di azzerare i riferimenti tornando a un provider prime.
  custom_endpoint_id: z.string().uuid().nullable().optional(),
  custom_model_id: z.string().uuid().nullable().optional(),
});
export type ArchitectConfigUpdateInput = z.infer<typeof ArchitectConfigUpdateInputSchema>;

// ---------------------------------------------------------------------------
// Capability matrix
// ---------------------------------------------------------------------------
export const ARCHITECT_CAPABILITY_TYPES = ['a2a', 'mcp'] as const;
export type ArchitectCapabilityType = (typeof ARCHITECT_CAPABILITY_TYPES)[number];
export const ArchitectCapabilityTypeSchema = z.enum(ARCHITECT_CAPABILITY_TYPES);

export const ArchitectCapabilityRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  capability_type: ArchitectCapabilityTypeSchema,
  capability_ref: z.string().min(1).max(255),
  is_allowed: z.boolean(),
  priority: z.number().int().min(1).max(10),
  requires_confirmation: z.boolean(),
  updated_by_user_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ArchitectCapabilityRow = z.infer<typeof ArchitectCapabilityRowSchema>;

export const ArchitectCapabilityCatalogItemSchema = z.object({
  type: ArchitectCapabilityTypeSchema,
  ref: z.string().min(1).max(255),
  name: z.string().min(1).max(255),
  description: z.string().max(2000).default(''),
  domain: z.string().max(64).default('other'),
  is_destructive: z.boolean().default(false),
  is_read_only: z.boolean().default(false),
  // Stato persistito (eventualmente assente per le capability mai configurate).
  is_allowed: z.boolean().default(false),
  priority: z.number().int().min(1).max(10).default(5),
  requires_confirmation: z.boolean().default(false),
});
export type ArchitectCapabilityCatalogItem = z.infer<typeof ArchitectCapabilityCatalogItemSchema>;

export const ArchitectCapabilityUpsertEntrySchema = z.object({
  capability_type: ArchitectCapabilityTypeSchema,
  capability_ref: z.string().min(1).max(255),
  is_allowed: z.boolean(),
  priority: z.number().int().min(1).max(10).default(5),
  requires_confirmation: z.boolean().default(false),
});
export type ArchitectCapabilityUpsertEntry = z.infer<typeof ArchitectCapabilityUpsertEntrySchema>;

export const ArchitectCapabilityUpsertBodySchema = z.object({
  entries: z.array(ArchitectCapabilityUpsertEntrySchema).min(1).max(2000),
});
export type ArchitectCapabilityUpsertBody = z.infer<typeof ArchitectCapabilityUpsertBodySchema>;

// ---------------------------------------------------------------------------
// Preset bundle
// ---------------------------------------------------------------------------
export const ARCHITECT_PRESETS = [
  'read-only-safe',
  'crm-messaging',
  'pos-readonly',
  'voice-handoff',
  'automation',
  'full-power',
] as const;
export type ArchitectPresetId = (typeof ARCHITECT_PRESETS)[number];
export const ArchitectPresetIdSchema = z.enum(ARCHITECT_PRESETS);

export const ArchitectApplyPresetBodySchema = z.object({
  preset_id: ArchitectPresetIdSchema,
});
export type ArchitectApplyPresetBody = z.infer<typeof ArchitectApplyPresetBodySchema>;

// ---------------------------------------------------------------------------
// Runs / hops / pending action
// ---------------------------------------------------------------------------
export const ARCHITECT_RUN_STATUSES = [
  'running',
  'completed',
  'failed',
  'awaiting_confirmation',
  'cancelled',
] as const;
export type ArchitectRunStatus = (typeof ARCHITECT_RUN_STATUSES)[number];
export const ArchitectRunStatusSchema = z.enum(ARCHITECT_RUN_STATUSES);

/**
 * Filtri + cursor pagination per `GET /api/architect/runs`.
 * - `status` (opzionale, multi) filtra per stato.
 * - `from`   (opzionale, ISO timestamp) inclusivo `started_at >= from`.
 * - `cursor` (opzionale, ISO timestamp) esclusivo `started_at < cursor`
 *   restituito come `meta.next_cursor` quando ci sono altre pagine.
 */
export const ArchitectRunListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  status: z
    .union([ArchitectRunStatusSchema, z.array(ArchitectRunStatusSchema)])
    .optional(),
  from: z.string().datetime().optional(),
  cursor: z.string().datetime().optional(),
});
export type ArchitectRunListQuery = z.infer<typeof ArchitectRunListQuerySchema>;

export const ArchitectHopSchema = z.object({
  hop_index: z.number().int().min(0),
  kind: z.enum(['llm', 'a2a', 'mcp', 'error', 'note']),
  started_at: z.string(),
  finished_at: z.string().optional(),
  duration_ms: z.number().int().nonnegative().optional(),
  capability_type: ArchitectCapabilityTypeSchema.optional(),
  capability_ref: z.string().optional(),
  args: z.unknown().optional(),
  result_summary: z.string().max(8000).optional(),
  ok: z.boolean(),
  error: z.string().max(2000).optional(),
  token_usage: z
    .object({
      prompt: z.number().int().nonnegative().default(0),
      completion: z.number().int().nonnegative().default(0),
      total: z.number().int().nonnegative().default(0),
    })
    .optional(),
});
export type ArchitectHop = z.infer<typeof ArchitectHopSchema>;

export const ArchitectPendingActionSchema = z.object({
  hop_index: z.number().int().nonnegative(),
  capability_type: ArchitectCapabilityTypeSchema,
  capability_ref: z.string(),
  tool_call_id: z.string(),
  tool_name: z.string(),
  args: z.record(z.string(), z.unknown()),
  human_reason: z.string().max(1000).optional(),
});
export type ArchitectPendingAction = z.infer<typeof ArchitectPendingActionSchema>;

export const ArchitectRootMessageSchema = z.object({
  text: z.string().min(1).max(20_000),
  context_id: z.string().max(255).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type ArchitectRootMessage = z.infer<typeof ArchitectRootMessageSchema>;

export const ArchitectRunSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  status: ArchitectRunStatusSchema,
  root_message: ArchitectRootMessageSchema,
  hops: z.array(ArchitectHopSchema),
  pending_action: ArchitectPendingActionSchema.nullable(),
  token_usage: z
    .object({
      prompt: z.number().int().nonnegative().default(0),
      completion: z.number().int().nonnegative().default(0),
      total: z.number().int().nonnegative().default(0),
    })
    .default({ prompt: 0, completion: 0, total: 0 }),
  cost_estimate_cents: z.number().int().nullable(),
  initiated_by_user_id: z.string().uuid().nullable(),
  error_message: z.string().nullable(),
  // Task #2692 — link alla schedule che ha avviato il run (null se run manuale).
  schedule_id: z.string().uuid().nullable().default(null),
  started_at: z.string(),
  finished_at: z.string().nullable(),
});
export type ArchitectRun = z.infer<typeof ArchitectRunSchema>;

export const ArchitectRunStartBodySchema = z.object({
  message: z.string().min(1).max(20_000),
  context_id: z.string().max(255).optional(),
});
export type ArchitectRunStartBody = z.infer<typeof ArchitectRunStartBodySchema>;

export const ArchitectRunConfirmBodySchema = z.object({
  decision: z.enum(['approve', 'reject']),
});
export type ArchitectRunConfirmBody = z.infer<typeof ArchitectRunConfirmBodySchema>;

// ---------------------------------------------------------------------------
// Schedules (Task #2692) — obiettivo NL ricorrente ri-eseguito dall'Architetto.
// ---------------------------------------------------------------------------
/**
 * Stato persistito di una schedule. `cron_expression` è validata server-side
 * con cron-parser (lo schema shared verifica solo lunghezza). `timezone` è un
 * nome IANA (es. `Europe/Rome`) usato per risolvere il cron in `next_run_at`.
 * `provider_snapshot`/`model_snapshot` sono informativi (foto config alla
 * creazione): il run usa la config live del tenant a ogni esecuzione.
 */
export const ArchitectScheduleSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  objective: z.string().min(1).max(20_000),
  cron_expression: z.string().min(1).max(128),
  timezone: z.string().min(1).max(64),
  is_enabled: z.boolean(),
  next_run_at: z.string().nullable(),
  last_run_at: z.string().nullable(),
  last_run_id: z.string().uuid().nullable(),
  provider_snapshot: ArchitectProviderSchema.nullable(),
  model_snapshot: z.string().max(128).nullable(),
  created_by_user_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ArchitectSchedule = z.infer<typeof ArchitectScheduleSchema>;

export const ArchitectScheduleCreateBodySchema = z.object({
  objective: z.string().min(1).max(20_000),
  cron_expression: z.string().min(1).max(128),
  timezone: z.string().min(1).max(64).default('Europe/Rome'),
  is_enabled: z.boolean().default(true),
});
export type ArchitectScheduleCreateBody = z.infer<typeof ArchitectScheduleCreateBodySchema>;

export const ArchitectScheduleUpdateBodySchema = z
  .object({
    objective: z.string().min(1).max(20_000).optional(),
    cron_expression: z.string().min(1).max(128).optional(),
    timezone: z.string().min(1).max(64).optional(),
    is_enabled: z.boolean().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: 'almeno un campo da aggiornare',
  });
export type ArchitectScheduleUpdateBody = z.infer<typeof ArchitectScheduleUpdateBodySchema>;

export const ArchitectScheduleListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  enabled: z
    .union([z.literal('true'), z.literal('false')])
    .transform((v) => v === 'true')
    .optional(),
});
export type ArchitectScheduleListQuery = z.infer<typeof ArchitectScheduleListQuerySchema>;

/**
 * Anteprima delle prossime esecuzioni per un cron+timezone, senza persistere
 * nulla. Usata dal cockpit per mostrare "prossime N esecuzioni" mentre l'utente
 * compone/edita una schedule. Il cron è validato server-side (cron-parser).
 */
export const ArchitectSchedulePreviewBodySchema = z.object({
  cron_expression: z.string().min(1).max(128),
  timezone: z.string().min(1).max(64).default('Europe/Rome'),
  count: z.coerce.number().int().min(1).max(10).default(3),
});
export type ArchitectSchedulePreviewBody = z.infer<typeof ArchitectSchedulePreviewBodySchema>;

export const ArchitectSchedulePreviewResultSchema = z.object({
  cron_expression: z.string(),
  timezone: z.string(),
  next_runs: z.array(z.string()),
});
export type ArchitectSchedulePreviewResult = z.infer<typeof ArchitectSchedulePreviewResultSchema>;

// ---------------------------------------------------------------------------
// Error codes (riusano spazio A2A_ERROR custom — coerenti con a2a-server)
// ---------------------------------------------------------------------------
export const ARCHITECT_ERROR = {
  A2A_CYCLE_DETECTED: -32010,
  A2A_SELF_CALL_FORBIDDEN: -32011,
  CAPABILITY_NOT_ALLOWED: -32012,
  HARD_LIMIT_EXCEEDED: -32013,
  RUN_NOT_FOUND: -32014,
  RUN_INVALID_STATE: -32015,
  SCHEDULE_NOT_FOUND: -32016,
  SCHEDULE_INVALID_CRON: -32017,
} as const;
export type ArchitectErrorCode = (typeof ARCHITECT_ERROR)[keyof typeof ARCHITECT_ERROR];

export const ARCHITECT_DEFAULT_SYSTEM_PROMPT = `Sei l'Agente Architetto del tenant: un super-orchestratore che pianifica e delega.
Hai accesso a due famiglie di strumenti, esposte come tool function:
- Agenti A2A del tenant (delega completa di un task verticale)
- Tool MCP interni (operazioni atomiche su CRM/POS/Calendar/Messaging/Telephony/AI/Files)

Regole operative:
1. Analizza la richiesta utente, scomponi in passi.
2. Per ogni passo scegli lo strumento più adatto: preferisci agenti A2A specializzati quando coprono interi flussi, altrimenti combina tool MCP.
3. NON chiamare mai te stesso. NON entrare in loop ripetendo lo stesso strumento senza progressi.
4. Per operazioni distruttive (DELETE, invio massivo, pagamenti) lo strumento può richiedere conferma: in quel caso il sistema sospende il run e l'utente approverà.
5. Comunica in italiano, conciso. Al termine, riassumi cosa è stato fatto.`;

export const ARCHITECT_AGENT_SLUG = 'architect';
export const ARCHITECT_LOCAL_KEY = 'local://architect';
